import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, ThumbsUp, ThumbsDown, Scale, Printer, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGetAIReview, type AiReviewData } from '@/api/bills-api';
import { printBulkAIReviews } from '@/utils/html/aiReviewHtml';
import { Bill } from '@/types/bill';
import { formatSessionCode } from '@/utils/date';
import { useToast } from "@/hooks/use-toast";
// test comment to push

interface BulkPrintDialogProps {
  bills: Bill[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// AiReviewData type is now imported from bills-api.ts

interface ImpactItemProps {
  description: string;
  rating: string;
  sentiment: string;
  billText: string;
  citation: string;
}

// Helper component for rendering individual impact items
const ImpactItem = ({ description, rating, sentiment, billText, citation }: ImpactItemProps) => {
  const sentimentStyles = {
    positive: "bg-green-100 text-green-800 print-positive",
    negative: "bg-red-100 text-red-800 print-negative",
    neutral: "bg-gray-100 text-gray-800 print-neutral"
  };

  const sentimentIcons = {
    positive: ThumbsUp,
    negative: ThumbsDown,
    neutral: Scale
  };

  const Icon = sentimentIcons[sentiment as keyof typeof sentimentIcons] || Scale;

  return (
    <div className="border rounded-md p-3 mb-3">
      <div className="flex justify-between items-start mb-2">
        <div className="font-medium">{description}</div>
        <Badge variant="secondary" className={cn(sentimentStyles[sentiment as keyof typeof sentimentStyles])}>
          <Icon className={`h-3 w-3 mr-1 ${sentiment === 'positive' ? 'thumb-up-icon' : sentiment === 'negative' ? 'thumb-down-icon' : 'scale-icon'}`} />
          Rating: {rating}
        </Badge>
      </div>

      {billText && (
        <div className="mt-2 text-sm">
          <div className="text-muted-foreground mb-1 font-semibold">Referenced Bill Text:</div>
          <div className="bg-gray-50 p-2 rounded italic text-sm">{billText}</div>
        </div>
      )}

      {citation && (
        <div className="mt-2 text-xs text-muted-foreground">
          Citation: {citation}
        </div>
      )}
    </div>
  );
};

export function BulkPrintDialog({ bills, open, onOpenChange }: BulkPrintDialogProps) {
  const [reviewsData, setReviewsData] = useState<Record<string, AiReviewData | null>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFetchingRef = useRef(false);
  
  const getAIReview = useGetAIReview();
  const { toast } = useToast();

  // Function to fetch a single bill's data with timeout
  const fetchSingleBill = useCallback(async (bill: Bill): Promise<AiReviewData | null> => {
    if (!bill.session || !bill.bill_number) {
      console.log(`Skipping bill ${bill.bill_number} - missing session or bill_number`);
      return null;
    }
    
    try {
      console.log(`Fetching review for bill ${bill.bill_number}, session ${bill.session}`);
      const formattedSession = formatSessionCode(bill.session);
      
      // Add a promise race with a timeout to prevent hanging
      const result = await Promise.race([
        getAIReview.mutateAsync({
          session: formattedSession,
          bill_number: bill.bill_number
        }),
        new Promise<null>((resolve) => {
          setTimeout(() => {
            console.log(`Timeout for bill ${bill.bill_number}`);
            resolve(null);
          }, 20000); // 20-second timeout per bill
        })
      ]);
      
      if (result) {
        console.log(`Received data for bill ${bill.bill_number}`);
        
        // Validate that the result has the expected structure
        if (!(result as AiReviewData).report_umd || !(result as AiReviewData).report_higher_ed) {
          console.error(`Invalid data structure for bill ${bill.bill_number}:`, result);
          return null;
        }
        
        return result as AiReviewData;
      } else {
        console.log(`Timed out or no data for bill ${bill.bill_number}`);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching AI review for bill ${bill.bill_number}:`, error);
      return null;
    }
  }, [getAIReview]);

  // Clear all timeouts on unmount or dialog close
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      console.log("Dialog opened with", bills.length, "bills");
      setReviewsData({});
      setProgress(0);
      setError(null);
      setIsLoading(true);
      isFetchingRef.current = false;
      
      // Set a master timeout to force completion after 30 seconds
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        console.log("Master timeout triggered, forcing completion");
        setIsLoading(false);
        // Only show error if we haven't loaded anything
        if (Object.keys(reviewsData).length === 0) {
          setError("Could not load reviews. There might be an issue with the API or network connection.");
          toast({
            title: "Loading Failed",
            description: "Failed to load bill reviews. Please try again later.",
            variant: "destructive"
          });
        }
      }, 60000);
    } else {
      // Clear timeout when dialog closes
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [open, toast, bills.length, reviewsData]);

  // Fetch data only once when dialog opens
  useEffect(() => {
    if (!open || bills.length === 0 || isFetchingRef.current) return;
    
    const fetchReviews = async () => {
      console.log("Starting to fetch reviews");
      isFetchingRef.current = true;
      setIsLoading(true);
      const newReviewsData: Record<string, AiReviewData | null> = {};
      
      try {
        // Immediately process up to 5 bills to handle more cases
        for (let i = 0; i < Math.min(bills.length, 5); i++) {
          const result = await fetchSingleBill(bills[i]);
          if (result) {
            newReviewsData[bills[i].bill_number] = result;
          } else {
            newReviewsData[bills[i].bill_number] = null;
          }
          setProgress(Math.round(((i + 1) / bills.length) * 100));
        }
        
        // Print debugging info
        console.log("Loaded reviews:", Object.keys(newReviewsData).length);
        if (Object.keys(newReviewsData).length === 0) {
          console.log("No reviews were loaded successfully");
          setError("No reviews could be loaded. There might be an issue with the bill data or API connection.");
        }
      } catch (e) {
        console.error("Error in fetchReviews:", e);
        setError(`Error loading reviews: ${e instanceof Error ? e.message : String(e)}`);
      } finally {
        setReviewsData(newReviewsData);
        setIsLoading(false);
        isFetchingRef.current = false;
        
        // Clear the timeout since we're done
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    };

    fetchReviews();
  }, [open, bills, fetchSingleBill]);

  const handlePrint = () => {
    // Use the iframe approach for consistent printing across browsers
    printBulkAIReviews(bills, reviewsData);
  };

  // Provide simple fallback content
  const renderFallbackContent = () => {
    return (
      <div className="bill-review-section my-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Bill Review Information</h2>
          {bills.map((bill, index) => (
            <div key={index} className="mb-6 p-4 border rounded">
              <h3 className="text-xl font-semibold mb-2">{bill.bill_number}</h3>
              <p className="text-gray-700 mb-2">{bill.title || 'No Title'}</p>
              <div className="text-sm text-gray-500">
                <p>Session: {bill.session || 'Unknown'}</p>
                {bill.synopsis && <p className="mt-2">Synopsis: {bill.synopsis}</p>}
              </div>
            </div>
          ))}
          <div className="mt-8 text-gray-700">
            <p>AI Review data could not be loaded.</p>
            <p>Please try viewing individual bill reviews instead.</p>
          </div>
        </div>
      </div>
    );
  };

  if (!open) return null;

  const loadedBillsCount = Object.keys(reviewsData).length;
  const totalBillsCount = bills.length;
  const hasAnyReviews = Object.values(reviewsData).some(data => data !== null);

  return (
    <Dialog open={open} onOpenChange={(newState) => {
      // Clear timeout when closing
      if (!newState && timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      onOpenChange(newState);
    }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto BulkPrintDialog__content">
        <DialogHeader className="print-header flex justify-between items-center">
          <DialogTitle className="flex items-center gap-2 page-title">
            <FileText className="h-5 w-5" />
            Legislative Reviews for {bills.length} Bill{bills.length !== 1 ? 's' : ''}
          </DialogTitle>
          <Button 
            onClick={handlePrint} 
            disabled={isLoading || (!hasAnyReviews && loadedBillsCount === totalBillsCount)}
            className="flex items-center gap-2 print-visible ml-auto"
            variant="outline"
            size="sm"
          >
            <Printer className="h-4 w-4" />
            Print
          </Button>
        </DialogHeader>

        <div className="grid gap-6 py-4 bulk-reviews-container">
          {isLoading ? (
            <div className="flex flex-col justify-center items-center py-12">
              <div className="flex items-center mb-4">
                <Loader2 className="h-8 w-8 animate-spin mr-2" />
                <span>Loading AI reviews... {loadedBillsCount} loaded so far</span>
              </div>
              <div className="w-full max-w-md mt-2">
                <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-2 bg-blue-600 rounded-full transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="text-xs text-center text-gray-500 mt-1">
                  {loadedBillsCount} of {totalBillsCount} bills loaded ({progress}%)
                </div>
              </div>
            </div>
          ) : hasAnyReviews ? (
            bills.map((bill, index) => {
              const reviewData = reviewsData[bill.bill_number];
              
              if (!reviewData) {
                return (
                  <div key={bill.bill_number} className="bill-review-section">
                    <h2 className="text-2xl font-bold mb-2 bill-heading">{bill.bill_number}</h2>
                    <h3 className="text-xl mb-4 bill-title">{bill.title || 'No Title'}</h3>
                    
                    <div className="text-center py-6 text-muted-foreground">
                      <AlertCircle className="h-6 w-6 mx-auto mb-2 text-amber-500" />
                      <p>No AI review data available for this bill.</p>
                      <Button 
                        onClick={async () => {
                          try {
                            const result = await fetchSingleBill(bill);
                            if (result) {
                              setReviewsData(prev => ({
                                ...prev,
                                [bill.bill_number]: result
                              }));
                              toast({
                                title: "Review Loaded",
                                description: `Successfully loaded review for ${bill.bill_number}`
                              });
                            } else {
                              toast({
                                title: "Loading Failed",
                                description: `Could not load the review for ${bill.bill_number}`,
                                variant: "destructive"
                              });
                            }
                          } catch (error) {
                            console.error(`Error retrying load for bill ${bill.bill_number}:`, error);
                            toast({
                              title: "Error",
                              description: `Failed to load review: ${error instanceof Error ? error.message : String(error)}`,
                              variant: "destructive"
                            });
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="mt-2"
                      >
                        Retry Loading
                      </Button>
                    </div>
                    
                    {index < bills.length - 1 && (
                      <div className="page-break"></div>
                    )}
                  </div>
                );
              }
              
              return (
                <div key={bill.bill_number} className="bill-review-section">
                  <h2 className="text-2xl font-bold mb-2 bill-heading">{bill.bill_number}</h2>
                  <h3 className="text-xl mb-4 bill-title">{bill.title || 'No Title'}</h3>
                  
                  <>
                    {/* UMD Impact Card */}
                    <Card className="mb-8">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xl">UMD Impact Analysis</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-sm">
                            {reviewData.report_umd?.impact_category || 'Unknown'}
                          </Badge>
                        </div>

                        <div className="text-sm mb-4">
                          <h4 className="font-semibold mb-1">Rationale:</h4>
                          <p className="text-muted-foreground">{reviewData.report_umd?.rationale || 'No rationale available'}</p>
                        </div>

                        <Separator />

                        <div>
                          <h4 className="font-semibold mb-3">Detailed Impacts:</h4>
                          {reviewData.report_umd?.impacts?.map((impact, impactIndex) => (
                            <ImpactItem
                              key={impactIndex}
                              description={impact.description}
                              rating={impact.rating}
                              sentiment={impact.sentiment}
                              billText={impact.bill_text}
                              citation={impact.citation}
                            />
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Higher Education Impact Card */}
                    <Card className="mb-8">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xl">Higher Education Impact</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="text-sm mb-4">
                          <h4 className="font-semibold mb-1">Summary:</h4>
                          <p className="text-muted-foreground">{reviewData.report_higher_ed?.summary || 'No summary available'}</p>
                        </div>

                        <Separator />

                        <div>
                          <h4 className="font-semibold mb-3">Detailed Impacts:</h4>
                          {reviewData.report_higher_ed?.impacts?.map((impact, impactIndex) => (
                            <ImpactItem
                              key={impactIndex}
                              description={impact.description}
                              rating={impact.rating}
                              sentiment={impact.sentiment}
                              billText={impact.bill_text}
                              citation={impact.citation}
                            />
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                  
                  {index < bills.length - 1 && (
                    <div className="page-break"></div>
                  )}
                </div>
              );
            })
          ) : error ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <AlertCircle className="h-10 w-10 text-amber-500 mb-2" />
              <h3 className="text-lg font-medium mb-2">Loading Issues</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              {renderFallbackContent()}
            </div>
          ) : (
            renderFallbackContent()
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
